// myapp/src/context/AdminAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import adminApi from '../api/adminApi';
import { User } from '../types';

interface AdminAuthContextType {
  user: User | null;
  role: string | null;
  permissions: string[];
  loading: boolean;
  login: (credentials: any) => Promise<any>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isAuthenticated: boolean;
  theme: string;
  toggleTheme: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Lightweight native JWT parser
const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const AdminAuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Theme configuration
  const [theme, setTheme] = useState<string>(localStorage.getItem('theme') || 'dark'); // Default to beautiful premium dark mode

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Session Verification
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_jwt_token');
      if (!token) {
        setUser(null);
        setRole(null);
        setPermissions([]);
        return;
      }

      // Decode JWT locally for instantaneous claims mapping
      const claims = parseJwt(token);
      if (claims) {
        const parsedRole = claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || claims["Role"] || null;
        setRole(parsedRole);
        const permsStr = claims["Permissions"] || '';
        setPermissions(permsStr ? permsStr.split(',') : []);
      }

      // Call API context lookup
      const res = await adminApi.get('/Auth/me');
      setUser(res.data);
      setRole(res.data.roleName);
    } catch (err) {
      console.warn("Admin auth lookup failed, clearing expired session tokens.", err);
      localStorage.removeItem('admin_jwt_token');
      setUser(null);
      setRole(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize session on startup
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Admin login request
  const login = async (credentials: any) => {
    const payload = {
      ...credentials,
      portalType: 'admin'
    };

    const res = await adminApi.post('/Auth/login', payload);
    const token = res.data.token;
    localStorage.setItem('admin_jwt_token', token);

    // Decode claims
    const claims = parseJwt(token);
    if (claims) {
      const parsedRole = claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || claims["Role"] || null;
      setRole(parsedRole);
      const permsStr = claims["Permissions"] || '';
      setPermissions(permsStr ? permsStr.split(',') : []);
    }

    setUser(res.data.user);
    return res.data;
  };

  // Admin session teardown
  const logout = async () => {
    try {
      await adminApi.post('/Auth/logout', {});
    } catch (e) {
      // Ignore API logout errors
    } finally {
      localStorage.removeItem('admin_jwt_token');
      setUser(null);
      setRole(null);
      setPermissions([]);

      // Redirect strictly to admin login screen on active domain
      window.location.href = '/admin/login';
    }
  };

  const value: AdminAuthContextType = {
    user,
    role,
    permissions,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    theme,
    toggleTheme,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};
